import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Heart, LogOut, Instagram } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";

const linkCls = ({ isActive }) =>
  `relative text-sm tracking-wide transition-colors duration-300 hover:text-white ${isActive ? "text-white" : "text-[#cbbbe6]"}`;

const NavLinks = ({ onClick }) => {
  const { user } = useAuth();
  return (
    <>
      <NavLink to="/shop" className={linkCls} onClick={onClick} data-testid="nav-shop-link">Shop</NavLink>
      <Link to="/#request" className="text-sm tracking-wide text-[#cbbbe6] transition-colors hover:text-white" onClick={onClick} data-testid="nav-request-link">Request an item</Link>
      <NavLink to="/reviews" className={linkCls} onClick={onClick} data-testid="nav-reviews-link">Reviews</NavLink>
      {user && user.role === "admin" && (
        <NavLink to="/admin" className={linkCls} onClick={onClick} data-testid="nav-admin-link">Dashboard</NavLink>
      )}
      {user && user.role !== "admin" && (
        <NavLink to="/account" className={linkCls} onClick={onClick} data-testid="nav-account-link">My claims</NavLink>
      )}
    </>
  );
};

export const Nav = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const out = async () => { await logout(); setOpen(false); nav("/"); };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#281245]/60 bg-[#07030c]/70 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 md:px-10">
        <Logo />
        <nav className="hidden items-center gap-9 md:flex"><NavLinks /></nav>
        <div className="hidden items-center gap-4 md:flex">
          <a href="https://www.instagram.com/shopmaisonpierre" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="nav-instagram-link" className="text-[#cbbbe6] transition-colors hover:text-white"><Instagram className="h-5 w-5" /></a>
          {user && user.role !== "admin" && (
            <Link to="/account?tab=saved" data-testid="nav-saved-link" className="flex items-center gap-1.5 text-sm text-[#cbbbe6] hover:text-white">
              <Heart className="h-4 w-4" /> <span className="font-mono-j">{user.saved?.length || 0}</span>
            </Link>
          )}
          {user ? (
            <button onClick={out} data-testid="nav-logout-button" className="flex items-center gap-2 rounded-full border border-[#3b1d63] px-4 py-2 text-sm text-[#e9ddfb] transition-colors hover:border-[#a855f7] hover:bg-[#a855f7]/10">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          ) : (
            <Link to="/login" data-testid="nav-login-button" className="rounded-full bg-[#f3eefc] px-5 py-2 text-sm font-medium text-[#07030c] transition-colors hover:bg-[#d8b4fe]">
              Sign in
            </Link>
          )}
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button className="md:hidden p-2 text-[#f3eefc]" data-testid="nav-mobile-menu-button" aria-label="Open menu"><Menu className="h-6 w-6" /></button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85vw] border-[#281245] bg-[#0b0514] text-[#f3eefc]">
            <SheetTitle className="font-display text-2xl text-[#f3eefc]">Menu</SheetTitle>
            <div className="mt-10 flex flex-col gap-7 text-lg [&_*]:text-lg">
              <NavLinks onClick={() => setOpen(false)} />
              <a href="https://www.instagram.com/shopmaisonpierre" target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="flex items-center gap-2" data-testid="mobile-instagram-link"><Instagram className="h-5 w-5 text-[#a855f7]" /> @shopmaisonpierre</a>
              {user ? (
                <button onClick={out} className="text-left text-[#d8b4fe]" data-testid="mobile-logout-button">Sign out</button>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="text-[#d8b4fe]" data-testid="mobile-login-link">Sign in</Link>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
